import React, { forwardRef, useMemo, useState } from "react";
import { Pressable, View } from "react-native";
import { FlashList } from "@shopify/flash-list";
import { type ActionSheetRef } from "react-native-actions-sheet";
import { AppText } from "@/components/ui/AppText";
import { SearchInput } from "@/components/ui/SearchInput";
import { WORLD_COUNTRIES } from "@/constants/countries";
import type { CountryOption } from "@/components/ui/PhoneInput";
import { AppActionSheet } from "@/components/sheets/ActionSheet";
import { useI18n } from "@/context/I18nContext";

export interface CountryPickerSheetProps {
  selected?: CountryOption;
  onSelect: (country: CountryOption) => void;
  onClose?: () => void;
}

export const CountryPickerSheet = forwardRef<ActionSheetRef, CountryPickerSheetProps>(
  function CountryPickerSheet({ selected, onSelect, onClose }, ref) {
    const { t } = useI18n();
    const [query, setQuery] = useState("");

    const filtered: CountryOption[] = useMemo(() => {
      if (!query.trim()) return WORLD_COUNTRIES;
      const lower = query.toLowerCase();
      return WORLD_COUNTRIES.filter((country) => country.name.toLowerCase().includes(lower));
    }, [query]);

    return (
      <AppActionSheet
        ref={ref}
        headerTitle="countryPicker.title"
        onClose={() => {
          setQuery("");
          onClose?.();
        }}
      >
        <View className="px-4 pb-2">
          <SearchInput
            label="common.search"
            value={query}
            onChangeText={setQuery}
            placeholder="common.searchCountriesPlaceholder"
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
            testID="country-picker-search"
          />
        </View>

        <View style={{ height: 360 }}>
          <FlashList
            data={filtered}
            keyExtractor={(country) => `${country.dialCode}-${country.name}`}
            renderItem={({ item: country }) => {
              const isSelected = selected?.name === country.name;

              return (
                <Pressable
                  onPress={() => onSelect(country)}
                  accessibilityRole="button"
                  accessibilityLabel={country.name}
                  accessibilityState={{ selected: isSelected }}
                  className={`flex-row items-center px-4 py-3 gap-3 ${
                    isSelected ? "bg-blue-5" : ""
                  }`}
                >
                  <AppText className="text-2xl leading-none">{country.flag}</AppText>
                  <AppText
                    weight={isSelected ? "semibold" : "regular"}
                    className="flex-1 text-base text-dark-text-50"
                    numberOfLines={1}
                  >
                    {country.name}
                  </AppText>
                </Pressable>
              );
            }}
            keyboardShouldPersistTaps="handled"
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: 24 }}
            ListEmptyComponent={
              <View className="items-center py-8">
                <AppText className="text-sm text-dark-text-10">
                  {t("common.noCountriesFound")}
                </AppText>
              </View>
            }
          />
        </View>
      </AppActionSheet>
    );
  }
);
